import { useQuery } from "@tanstack/react-query";
import type {
  DashboardAreaDataStatus,
  DashboardOverviewResponse,
  DashboardPopulationUnavailableReason,
  DashboardSource,
  DashboardSourceAccessStatus,
} from "@situate/contracts/runtime";
import { dashboardOverviewResponseSchema } from "@situate/contracts/runtime-zod";
import type { Deployment } from "@/features/deployment-selection/model/deployment";
import {
  canViewDashboard,
  canViewDashboardSource,
} from "@/features/deployment-selection/model/permissions";
import {
  createDashboardSourceState,
  type DashboardAreaMetric,
  type DashboardAreaRow,
  type DashboardMonitoringData,
  type DashboardMonitoringDataState,
  type DashboardAlertPreviewItem,
  type DashboardWidgetStatus,
} from "@/features/deployment-dashboard/overview/model/dashboard-monitoring-data";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";

const DASHBOARD_OVERVIEW_PATH = "/v1/dashboard/overview";
const DASHBOARD_STALE_TIME_MS = 10_000;
const DASHBOARD_REFETCH_INTERVAL_MS = 15_000;

interface DashboardMonitoringScope {
  deploymentId: string;
  deployment: Deployment;
}

function predictedStatus(deployment: Deployment, source: DashboardSource): DashboardWidgetStatus {
  return canViewDashboardSource(deployment, source) ? "loading" : "not_permitted";
}

function createPredictedData(
  deployment: Deployment,
  error: Error | null = null
): DashboardMonitoringData {
  const status = (source: DashboardSource): DashboardWidgetStatus =>
    error != null && canViewDashboardSource(deployment, source)
      ? "error"
      : predictedStatus(deployment, source);

  return {
    snapshotAt: null,
    timezone: null,
    dataStatus: null,
    population: createDashboardSourceState(status("population")),
    capacity: createDashboardSourceState(status("capacity")),
    throughput: createDashboardSourceState(status("throughput")),
    areas: createDashboardSourceState(status("areas")),
    alerts: createDashboardSourceState(status("notifications")),
  };
}

function getSourceAccess(
  response: DashboardOverviewResponse,
  source: DashboardSource
): DashboardSourceAccessStatus {
  switch (source) {
    case "areas":
      return response.source_access.areas;
    case "population":
      return response.source_access.population;
    case "capacity":
      return response.source_access.capacity;
    case "throughput":
      return response.source_access.throughput;
    case "notifications":
      return response.source_access.notifications;
  }
}

function permittedSourceStatus(
  response: DashboardOverviewResponse,
  source: DashboardSource,
  empty: boolean
): DashboardWidgetStatus {
  if (getSourceAccess(response, source) === "not_permitted") return "not_permitted";
  if (response.data_status === "unavailable") return "unavailable";
  if (empty) return "empty";
  if (response.data_status === "partial") return "partial";
  return "ready";
}

function areaMetricStatus(status: DashboardAreaDataStatus): DashboardWidgetStatus {
  if (status === "current") return "ready";
  return status;
}

function unavailableAreaMetric(status: DashboardWidgetStatus): DashboardAreaMetric {
  return { status, value: null };
}

function unavailablePopulationMetric(
  reason: DashboardPopulationUnavailableReason | undefined
): DashboardAreaMetric {
  return {
    status: reason === "not_configured" ? "partial" : "unavailable",
    value: null,
    ...(reason == null ? {} : { unavailableReason: reason }),
  };
}

function mapAlerts(response: DashboardOverviewResponse): DashboardAlertPreviewItem[] {
  return response.data.notifications.map((alert) => ({
    id: alert.notification_id,
    title: alert.title,
    zone: alert.area_display_name ?? "Deployment",
    severity: alert.severity,
    status: alert.status,
    detectedAt: new Date(alert.raised_at).toLocaleString(undefined, {
      timeZone: response.timezone,
    }),
  }));
}

/** Maps the canonical API snapshot without joining source records by position or display name. */
export function mapDashboardOverviewResponse(
  response: DashboardOverviewResponse
): DashboardMonitoringData {
  const population = response.data.population;
  const capacity = response.data.capacity;
  const areas = response.data.areas;
  const populationByArea = new Map(population?.by_area.map((item) => [item.area_id, item]));
  const capacityByArea = new Map(capacity?.by_area.map((item) => [item.area_id, item]));
  const populationAccess = response.source_access.population;
  const capacityAccess = response.source_access.capacity;

  const areaRows: DashboardAreaRow[] = (areas?.items ?? []).map((area) => {
    const areaPopulation = populationByArea.get(area.area_id);
    const areaCapacity = capacityByArea.get(area.area_id);
    const populationMetric =
      populationAccess === "not_permitted"
        ? unavailableAreaMetric("not_permitted")
        : areaPopulation == null
          ? unavailableAreaMetric("unavailable")
          : areaPopulation.current_population == null
            ? unavailablePopulationMetric(areaPopulation.unavailable_reason)
            : {
                status: areaMetricStatus(areaPopulation.data_status),
                value: areaPopulation.current_population,
                ...(areaPopulation.observed_at == null
                  ? {}
                  : { observedAt: areaPopulation.observed_at }),
                ...(areaPopulation.unavailable_reason == null
                  ? {}
                  : { unavailableReason: areaPopulation.unavailable_reason }),
              };
    const capacityMetric =
      capacityAccess === "not_permitted"
        ? unavailableAreaMetric("not_permitted")
        : areaCapacity == null || areaCapacity.capacity == null
          ? unavailableAreaMetric("unavailable")
          : { status: "ready" as const, value: areaCapacity.capacity };

    return {
      id: area.area_id,
      name: area.area_display_name,
      population: populationMetric,
      capacity: capacityMetric,
      derivedPopulation: unavailableAreaMetric("unavailable"),
    };
  });

  const hasStalePopulation =
    population?.by_area.some((item) => item.data_status === "stale") ?? false;
  const hasUnavailablePopulation =
    population?.by_area.some((item) => item.data_status === "unavailable") ?? false;
  let populationStatus = permittedSourceStatus(
    response,
    "population",
    population != null && population.reporting_areas === 0
  );
  if (populationStatus === "ready" && hasStalePopulation) populationStatus = "stale";
  if (populationStatus === "ready" && hasUnavailablePopulation) populationStatus = "partial";

  return {
    snapshotAt: response.snapshot_at,
    timezone: response.timezone,
    dataStatus: response.data_status,
    population: createDashboardSourceState(
      response.source_access.population === "not_permitted" || population == null
        ? response.source_access.population === "not_permitted"
          ? "not_permitted"
          : "unavailable"
        : populationStatus,
      population == null
        ? null
        : {
            currentPopulation: population.current_population,
            peakPopulationToday: population.peak_population_today,
            peakRecordedAt: population.peak_recorded_at,
          }
    ),
    capacity: createDashboardSourceState(
      response.source_access.capacity === "not_permitted" || capacity == null
        ? response.source_access.capacity === "not_permitted"
          ? "not_permitted"
          : "unavailable"
        : permittedSourceStatus(response, "capacity", capacity.by_area.length === 0),
      capacity == null ? null : { maxCapacity: capacity.max_capacity }
    ),
    throughput: createDashboardSourceState(
      response.source_access.throughput === "not_permitted" || response.data.throughput == null
        ? response.source_access.throughput === "not_permitted"
          ? "not_permitted"
          : "unavailable"
        : permittedSourceStatus(response, "throughput", response.data.throughput.today == null),
      response.data.throughput == null
        ? null
        : {
            throughputToday: response.data.throughput.today,
            throughputSince: response.data.throughput.since,
          }
    ),
    areas: createDashboardSourceState(
      response.source_access.areas === "not_permitted" || areas == null
        ? response.source_access.areas === "not_permitted"
          ? "not_permitted"
          : "unavailable"
        : permittedSourceStatus(response, "areas", areas.items.length === 0),
      areas == null ? null : areaRows
    ),
    alerts: createDashboardSourceState(
      permittedSourceStatus(response, "notifications", response.data.notifications.length === 0),
      response.source_access.notifications === "not_permitted" ? null : mapAlerts(response)
    ),
  };
}

/** Fetches and polls the permission-aware production dashboard overview snapshot. */
export function useDashboardMonitoringData({
  deploymentId,
  deployment,
}: DashboardMonitoringScope): DashboardMonitoringDataState {
  const runtime = useRuntimeHttpClient();
  const client = runtime.client;
  const hasDashboardGrant = runtime.operationGrants.some(
    (grant) => grant.operation_id === "dashboard_overview"
  );
  const query = useQuery<DashboardMonitoringData>({
    queryKey: ["dashboard-overview", deploymentId],
    queryFn: async () => {
      const rawResponse = await client!.get<unknown>(DASHBOARD_OVERVIEW_PATH);
      const response = dashboardOverviewResponseSchema.parse(rawResponse);
      return mapDashboardOverviewResponse(response);
    },
    enabled:
      Boolean(client) &&
      runtime.deploymentId === deploymentId &&
      hasDashboardGrant &&
      canViewDashboard(deployment),
    staleTime: DASHBOARD_STALE_TIME_MS,
    refetchInterval: DASHBOARD_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    placeholderData: (previousData) => previousData,
  });
  const error = query.error instanceof Error ? query.error : null;

  return {
    data: query.data ?? createPredictedData(deployment, error),
    loading: query.isLoading,
    refreshing: query.isFetching && query.data != null,
    error,
  };
}
